import React, { useState, useRef, useEffect } from 'react'
import { Button, Typography, Box, Stack, Checkbox, FormControlLabel, IconButton, Table, TableHead, TableRow, TableBody, TableCell } from '@mui/material'
import { useConfirm } from '@components/ConfirmProvider'
import { useIsMobile } from '@/hooks/Responsive'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import BasicModal from '@components/Modal/BasicModal'
import DOMPurify from 'dompurify'

export default function Step2({ onNext, terms, setTerms, reset }) {
  const { isMobile } = useIsMobile()
  const { alert, confirm } = useConfirm()
  
  const [termsText1, setTermsText1] = useState('')
  const [safeTermsText1, setSafeTermsText1] = useState('')
  
  const [openKey, setOpenKey] = useState(null)

  const [pendingAgreeKey, setPendingAgreeKey] = useState(null)

  useEffect(() => {
    fetch('/policy/terms/terms.html')
      .then(res => res.text())
      .then((html) => {
        setTermsText1(html)

        const clean = DOMPurify.sanitize(html, {
          USE_PROFILES: { html: true },
        })

        setSafeTermsText1(clean)
      })
  }, [])

  const termsText2 = (
    <Stack spacing={3} sx={{ color: 'text.secondary' }}>
      {/* 0. 제목 */}
      <Typography variant="body2" fontWeight={600} color="text.primary">
        OpenVLab 웹사이트에서는 개인정보처리방침에 의거하여 최초 회원가입 당시 아래와 같은 개인정보를 수집하고 있습니다.
      </Typography>

      {/* 1. 개인정보 수집 및 이용 목적 */}
      <Box>
        <Typography variant="body2" fontWeight={600} color="text.primary" sx={{ mb: 1 }}>
          1. 개인정보의 수집 및 이용 목적
        </Typography>

        <Typography variant="body2">
          OpenVLab은 서비스 이용과 관련하여 「개인정보 보호법」 제15조(개인정보의 수집·이용) 및 
          동법 시행령 제17조(동의를 받는 방법)에 따라 홈페이지에 회원가입을 신청하시는 분께 
          다음과 같은 이유로 필요한 최소한의 개인정보를 수집·이용합니다.
        </Typography>

        <Stack spacing={1.2} sx={{ mt: 1 }}>
          <Typography variant="body2">가. 회원관리</Typography>

          <Typography variant="body2">
            1) 회원 가입의사 확인, 회원제 서비스 제공에 따른 본인 식별 및 인증, 회원자격 유지 및 관리,
            제한적 본인확인제 시행을 따른 본인확인
          </Typography>

          <Typography variant="body2">
            2) 불량회원의 부정 이용방지와 비인가 사용방지
          </Typography>

          <Typography variant="body2">
            3) 가입의사 확인, 가입 및 가입횟수 제한
          </Typography>

          <Typography variant="body2">
            4) 이용문의, 체험예약 등을 위한 원활한 의사소통 경로의 확보, 각종 고지·통지
          </Typography>

          <Typography variant="body2">
            OpenVLab은 이용자들의 개인정보를 개인정보 처리목적에서 고지한 범위 내에서 사용하며, 
            이용자의 사전동의 없이는 고지한 범위를 초과하여 이용하거나 개인정보를 외부에 공개하지 않습니다.
            다만 아래 항목 경우 예외로 합니다.
          </Typography>

          <Typography variant="body2">예외1) 이용자가 사전에 동의한 경우</Typography>
          <Typography variant="body2">예외2) 법령에 의하거나, 수사 목적으로 법령에 정해진 절차에 따른 경우</Typography>
        </Stack>
      </Box>

      {/* 2. 수집 항목 */}
      <Box>
        <Typography variant="body2" fontWeight={600} color="text.primary" sx={{ mb: 1 }}>
          2. 개인정보의 수집 항목
        </Typography>

        <Typography variant="body2">
          서비스 이용을 위한 최소한의 개인정보를 수집합니다.
        </Typography>

        <Stack spacing={1} sx={{ mt: 1 }}>
          <Typography variant="body2">1) 필수 정보 — 이름, 생년월일, 휴대전화번호, 이메일, 성별</Typography>
          <Typography variant="body2">2) 선택 정보 —</Typography>
        </Stack>

        <Typography variant="body2" sx={{ mt: 1 }}>
          OpenVLab 홈페이지 내 개별 서비스 이용, 이벤트 응모 및 경품 신청 과정에서
          해당 서비스 이용자에 한해 추가 개인정보 수집이 발생할 수 있습니다.
          추가 수집 시점에서 ‘수집 항목, 이용 목적, 보유 기간 등’을 별도 안내 후 동의를 받습니다.
        </Typography>
      </Box>

      {/* 3. 자동 수집 항목 */}
      <Box>
        <Typography variant="body2" fontWeight={600} color="text.primary" sx={{ mb: 1 }}>
          3. 자동 수집되는 개인정보 항목
        </Typography>

        <Typography variant="body2">
          서비스 이용과정에서 편의를 제공하기 위해 아래 정보가 자동 생성되어 수집될 수 있습니다.
        </Typography>

        <Typography variant="body2" sx={{ mt: 1 }}>
          - IP 주소, 방문일시, 서비스 이용 기록, 쿠키
        </Typography>
      </Box>

      {/* 4. 보유 및 이용 기간 */}
      <Box>
        <Typography variant="body2" fontWeight={600} color="text.primary" sx={{ mb: 1 }}>
          4. 개인정보의 처리 및 보유기간
        </Typography>

        <Typography variant="body2" fontWeight={600}>
          OpenVLab은 법령 또는 동의받은 기간 내에서 개인정보를 보유·처리하며,
          기간 경과 시 별도로 동의를 받습니다.
        </Typography>

        <Stack spacing={1} sx={{ mt: 1 }}>
          <Typography variant="body2" fontWeight={600}>1) 보유 기간 — 회원 탈퇴 후 2년 보관 후 파기</Typography>

          <Typography variant="body2" fontWeight={600}>
            ※ 단, 아래 정보는 별도 보관합니다.
          </Typography>

          <Typography variant="body2" fontWeight={600}>- 이메일 주소: 중복가입 방지 및 게시물 관리 목적 (영구 보관)</Typography>

          <Typography variant="body2" fontWeight={600}>2) 보유 근거 — 정보주체의 동의</Typography>

          <Typography variant="body2" fontWeight={600}>
            아래 사유 발생 시 사유 종료 시까지 보관합니다:
          </Typography>

          <Typography variant="body2" fontWeight={600}>1) 법령 위반 수사·조사 진행 시 → 종료 시까지</Typography>
          <Typography variant="body2" fontWeight={600}>2) 채권·채무 관계 존재 시 → 정산 완료 시까지</Typography>
        </Stack>
      </Box>

      {/* 5. 동의 거부 권리 */}
      <Box>
        <Typography variant="body2" fontWeight={600} color="text.primary" sx={{ mb: 1 }}>
          5. 개인정보 수집에 대한 거부 권리 및 서비스 제한
        </Typography>

        <Typography variant="body2">
          이용자는 개인정보 수집에 대해 동의를 거부할 권리가 있으나,
          필수 항목 수집 동의 거부 시 회원가입 및 서비스 이용이 불가능합니다.
        </Typography>
      </Box>
    </Stack>
  )

  const termsText3 = (
    <>
      <Table
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          '& th, & td': {
            border: '1px solid',
            borderColor: 'divider',
            textAlign: 'center',
          },
          '& th': {
            backgroundColor: 'primary.50',
            fontWeight: 500,
          },
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell>제공받는 기관</TableCell>
            <TableCell>제공목적</TableCell>
            <TableCell>제공항목</TableCell>
            <TableCell>보유 및 이용기간</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          <TableRow>
            <TableCell>한국방송통신대학교</TableCell>
            <TableCell>-</TableCell>
            <TableCell>-</TableCell>
            <TableCell>-</TableCell>
          </TableRow>
        </TableBody>
      </Table>

      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
        위의 개인정보 제공에 동의를 거부할 권리가 있습니다.
        <br />
        동의를 거부할 경우 서비스 이용에 제한을 받을 수 있습니다.
      </Typography>
    </>
  )

  const refAgree1 = useRef(null)
  const [highlightEl, setHighlightEl] = useState(null)

  const findFirstUnagreed = () => {
    if (terms.agree1 !== 'agree') return refAgree1
    return null
  }

  const handleSubmit = async () => {
    const badRef = findFirstUnagreed()

    if (badRef) {
      await alert("필수 약관에 동의해 주세요.")

      if (!badRef.current) return
      badRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })

      const checkbox = badRef.current.querySelector('input[type="checkbox"]')
      if (checkbox) {
        setHighlightEl(checkbox)
        setTimeout(() => setHighlightEl(null), 2000)
      }
      return
    }

    onNext()
  }

  const TermsCheckCard = React.forwardRef(function TermsCheckCard({ checked, onChange, labelText, required, optional, onOpen, sx }, ref) {
    return (
      <Box
        ref={ref}
        sx={{
          p: 2,
          border: '1px solid',
          borderColor: 'grey.200',
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          ...sx,
        }}
      >
        <FormControlLabel
          control={
            <Checkbox
              checked={checked}
              onChange={onChange}
              sx={{
                transition: '0.25s',
                outline:
                  highlightEl ===
                  ref?.current?.querySelector('input[type="checkbox"]')
                    ? '3px solid'
                    : 'none',
                outlineColor: 'primary.main',
                outlineOffset: '-4px',
              }}
            />
          }
          label={
            <Typography fontWeight={checked ? 500 : 400}>
              {labelText}{' '}
              {required && (
                <Box component="span" sx={{ color: 'primary.main', fontWeight: 500 }}>
                  [필수]
                </Box>
              )}
              {optional && (
                <Box component="span" sx={{ color: 'text.secondary' }}>
                  [선택]
                </Box>
              )}
            </Typography>
          }
        />

        <IconButton onClick={onOpen}>
          <ChevronRightIcon />
        </IconButton>
      </Box>
    )
  })

  function TermsInfoCard({ label, onOpen }) {
    return (
      <Box
        sx={{
          p: 2,
          border: '1px solid',
          borderColor: 'grey.200',
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography fontWeight={500}>{label}</Typography>

        <IconButton onClick={onOpen}>
          <ChevronRightIcon />
        </IconButton>
      </Box>
    )
  }

  return (
    <Box>
      <Typography component="h3" variant={isMobile ? 'h6' : 'h5'} sx={{ mb: 1.5 }}>
        약관동의
      </Typography>

      <Typography
        component="p"
        variant={isMobile ? 'body1' : 'subtitle2'}
        fontWeight={400}
        sx={{ mb: isMobile ? 4 : 6 }}
      >
        실명 인증된 계정(이메일 주소)으로 가입하며, 필수 동의 사항 외에 개인정보
        수집·처리 방침(선택)을 포함합니다.
      </Typography>

      <Stack spacing={2}>

        {/* 필수 1 */}
        <TermsCheckCard
          ref={refAgree1}
          checked={terms.agree1 === 'agree'}
          onChange={(e) => {
            const checked = e.target.checked

            setTerms(prev => ({
              ...prev,
              agree1: e.target.checked ? 'agree' : '',
            }))

            if (checked) {
              setOpenKey('agree1')
            }
          }}
          labelText="이용약관"
          required
          onOpen={() => {
            setPendingAgreeKey('agree1')
            setOpenKey('agree1')
          }}
        />

        {/* 안내 항목 (체크박스 없음) */}
        <TermsInfoCard
          label="개인정보 수집 및 이용 안내"
          onOpen={() => setOpenKey('info')}
        />

        {/* 필수 2 */}
        <TermsCheckCard
          sx={{ display: 'none' }}
          checked={terms.agree2 === 'agree'}
          onChange={(e) => {
            const checked = e.target.checked

            setTerms(prev => ({
              ...prev,
              agree2: e.target.checked ? 'agree' : '',
            }))

            if (checked) {
              setOpenKey('agree2')
            }
          }}
          labelText="제3자 정보제공 동의"
          required
          onOpen={() => {
            setPendingAgreeKey('agree2')
            setOpenKey('agree2')
          }}
        />

      </Stack>

      {/* 팝업 */}
      <BasicModal
        open={!!openKey}
        onClose={() => {
          setOpenKey(null)
          setPendingAgreeKey(null)
        }}
        variant="bottomsheet"
        title={
          openKey === 'agree1'
            ? '이용약관'
            : openKey === 'info'
            ? '개인정보 수집 및 이용 안내'
            : openKey === 'agree2'
            ? '제3자 정보제공 동의'
            : ''
        }
        actions={
          <Button
            size="medium"
            variant="contained"
            color="primary"
            onClick={() => setOpenKey(null)}
            sx={{ width: isMobile ? '100%' : 'auto' }}
          >
            확인
          </Button>
        }
      >
        <Box>
          {openKey === 'agree1' && (
            <Box
              sx={{ typography: 'body2', color: 'text.secondary' }}
              dangerouslySetInnerHTML={{ __html: safeTermsText1 }}
            />
          )}

          {openKey === 'info' && (
            <Box sx={{ typography: 'body2', color: 'text.secondary' }}>
              {termsText2}
            </Box>
          )}

          {openKey === 'agree2' && (
            <Box sx={{ typography: 'body2', color: 'text.secondary' }}>
              {termsText3}
            </Box>
          )}
        </Box>
      </BasicModal>

      {/* <Stack spacing={6}>
        <TermsCard
          ref={refAgree1}
          checked={terms.agree1 === 'agree'}
          onChange={(e) => setTerms(prev => ({ ...prev, agree1: e.target.checked ? 'agree' : '' }))}
          label="이용약관 [필수]"
        >
          {termsText1}
        </TermsCard>

        <TermsCard
          ref={refAgree2}
          checked={terms.agree2 === 'agree'}
          onChange={(e) => setTerms(prev => ({ ...prev, agree2: e.target.checked ? 'agree' : '' }))}
          label="제3자 정보제공 동의 [필수]"
        >
          {termsText2}
        </TermsCard>

        <TermsCard
          ref={refAgree3}
          checked={terms.agree3 === 'agree'}
          onChange={(e) => setTerms(prev => ({ ...prev, agree3: e.target.checked ? 'agree' : '' }))}
          label="개인정보 수집 및 처리방침 [필수]"
        >
          {termsText3}
        </TermsCard>

        <TermsCard
          checked={terms.agree4opt === 'agree'}
          onChange={(e) => setTerms(prev => ({ ...prev, agree4opt: e.target.checked ? 'agree' : '' }))}
          label="개인정보 수집 및 처리방침 [선택]"
        >
          {termsText4}
        </TermsCard>
      </Stack> */}

      <Stack direction="row" justifyContent="flex-end" sx={{ mt: 5 }}>
        <Button size="large" variant="contained" onClick={handleSubmit}>
          다음
        </Button>
      </Stack>
    </Box>
  )
}
